import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["hiddenInput", "deviceList", "loading", "error", "selectedDevice"]
  static values = {
    url: String,
    selected: String,
    purchaseReportId: String
  }

  connect() {
    this.loadDevices()
  }

  async loadDevices() {
    this.showLoading()
    this.hideError()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      if (this.purchaseReportIdValue) {
        url.searchParams.append('purchase_report_id', this.purchaseReportIdValue)
      }

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch devices')
      }

      const data = await response.json()
      data.devices?.forEach(d => {
        console.log('Device:', d.device?.name, 'Browsers:', d.browsers?.map(b => ({ name: b.name, key: b.key })))
      })
      this.renderDeviceList(data.devices || [])
    } catch (error) {
      console.error('Error loading devices:', error)
      this.showError()
    } finally {
      this.hideLoading()
    }
  }

  renderDeviceList(deviceConfigurations) {
    if (!this.hasDeviceListTarget) return

    const container = this.deviceListTarget
    container.innerHTML = ''

    if (deviceConfigurations.length === 0) {
      container.innerHTML = '<p class="text-gray-500 text-sm">No devices available</p>'
      return
    }

    deviceConfigurations.forEach(deviceConfig => {
      const deviceElement = this.createDeviceElement(deviceConfig)
      container.appendChild(deviceElement)
    })

    if (this.selectedValue) {
      this.selectDeviceByName(this.selectedValue)
    }
  }

  createDeviceElement(deviceConfig) {
    const wrapper = document.createElement('div')
    wrapper.className = 'device-card mb-3'

    const deviceIconClass = this.getDeviceIconClass(deviceConfig)
    const deviceName = this.getDeviceBaseName(deviceConfig)
    const browsers = deviceConfig.browsers || []

    wrapper.innerHTML = `
      <div class="text-sm text-gray-700 mb-1">${this.escapeHtml(deviceName)}</div>
      <div class="flex items-center gap-1">
        <div class="flex-shrink-0 flex items-center justify-center">
          <span class="${deviceIconClass}" style="font-size: 36px;"></span>
        </div>
        ${browsers.map(b => {
          const iconClass = this.getBrowserIconClass(b)
          const browserName = b.name || b.key || 'Browser'
          return `
          <button type="button"
                  class="browser-option flex-shrink-0 border border-gray-300 rounded p-1 bg-white flex items-center justify-center hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer"
                  style="width: 36px; height: 36px;"
                  data-device-name="${this.escapeHtml(this.getDeviceDisplayName(deviceConfig, b))}"
                  data-action="click->device-select#selectBrowser"
                  title="${this.escapeHtml(browserName)}">
            <span class="${iconClass}"></span>
          </button>
        `}).join('')}
      </div>
    `

    return wrapper
  }

  getDeviceIconClass(deviceConfig) {
    const iconName = deviceConfig.device?.icon_name || deviceConfig.icon_name

    if (iconName) {
      if (iconName === 'computer-mac-os') {
        return `icon-${iconName}-lg`
      }
      let normalizedIcon = iconName.replace('computer', 'desktop')
      return `icon-${normalizedIcon}-lg`
    }

    let category = deviceConfig.device?.category?.key || deviceConfig.category?.key || deviceConfig.category || 'desktop'
    let os = deviceConfig.device?.operating_system?.key || deviceConfig.operating_system?.key || ''

    if (category === 'computer') category = 'desktop'

    const osIconMapping = {
      'mac-os': 'apple',
      'macos': 'apple',
      'ios': 'apple'
    }

    const mappedOs = osIconMapping[os] || os

    let iconClass = `icon-${category}`

    if (mappedOs) {
      iconClass = `icon-${category}-${mappedOs}`
    }

    return `${iconClass}-lg`
  }

  getBrowserIconClass(browser) {
    const browserKey = browser.key || browser.name || ''
    if (!browserKey) return `text-icon text-link icon-globe`

    const browsersWithIcons = [
      'android-browser', 'brave', 'chrome', 'edge', 'edge-chromium',
      'firefox', 'ie-6', 'ie-7', 'ie-8', 'ie-9', 'ie-10', 'ie-11',
      'opera', 'opera-coast', 'opera-mini', 'safari', 'samsung-browser', 'yandex'
    ]

    if (!browsersWithIcons.includes(browserKey)) {
      return `text-icon text-link icon-globe`
    }

    return `text-icon text-link icon-${browserKey}`
  }

  getDeviceBaseName(deviceConfig) {
    const deviceInfo = deviceConfig.device || deviceConfig
    const category = deviceInfo.category?.name || ''
    const vendor = deviceInfo.vendor?.name || ''
    const deviceName = deviceInfo.name || 'Unknown Device'
    const osVersion = deviceConfig.operating_system_version?.name || ''

    let name = ''
    if (category === 'Computers') {
      name = 'Desktop'
    } else if (vendor && vendor !== 'Unknown') {
      name = `${vendor} ${deviceName}`
    } else {
      name = deviceName
    }

    const osName = deviceInfo.operating_system?.name || ''
    if (osName && osVersion) {
      name = `${name} | ${osName} ${osVersion}`
    } else if (osVersion) {
      name = `${name} | ${osVersion}`
    }

    return name
  }

  getDeviceDisplayName(deviceConfig, browser = null) {
    const baseName = this.getDeviceBaseName(deviceConfig)
    const deviceInfo = deviceConfig.device || deviceConfig
    const categoryKey = deviceInfo.category?.key || ''
    const browserName = browser?.name || ''

    return [baseName, browserName, categoryKey].filter(Boolean).join(' | ')
  }

  selectBrowser(event) {
    event.preventDefault()
    const button = event.currentTarget
    const deviceName = button.dataset.deviceName

    this.deviceListTarget.querySelectorAll('.browser-option').forEach(el => {
      el.classList.remove('border-blue-500', 'bg-blue-100', 'border-2')
      el.classList.add('border-gray-300')
    })

    button.classList.remove('border-gray-300')
    button.classList.add('border-blue-500', 'bg-blue-100', 'border-2')

    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = deviceName
    }
  }

  selectDeviceByName(name) {
    if (!name) return

    let browserButton = this.deviceListTarget.querySelector(`[data-device-name="${name}"]`)

    if (!browserButton) {
      const buttons = this.deviceListTarget.querySelectorAll('.browser-option')
      browserButton = Array.from(buttons).find(btn => btn.dataset.deviceName === name)
    }

    if (browserButton) {
      browserButton.classList.remove('border-gray-300')
      browserButton.classList.add('border-blue-500', 'bg-blue-100', 'border-2')
    }
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
    if (this.hasDeviceListTarget) {
      this.deviceListTarget.classList.add('opacity-50')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
    if (this.hasDeviceListTarget) {
      this.deviceListTarget.classList.remove('opacity-50')
    }
  }

  showError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove('hidden')
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML.replace(/"/g, '&quot;')
  }
}
